import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Loader2, Download, MessageSquare, Check } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";
import { Ambience, Mono, Swap, useAudience } from "./NetworkAtoms";

const dark = "bg-white/[0.04] border-white/12 text-white placeholder:text-slate-600 focus:border-[#0055FF] focus-visible:ring-0";

export default function NetworkClose() {
  const { audience } = useAudience();
  const [enquiry, setEnquiry] = useState(false);

  return (
    <section className="relative overflow-hidden border-t border-white/[0.07]" data-testid="network-close">
      <div aria-hidden className="absolute inset-0 bg-[radial-gradient(ellipse_at_50%_120%,rgba(0,85,255,0.28),transparent_62%)]" />
      <Ambience beams={false} />

      <div className="relative mx-auto max-w-7xl px-5 sm:px-8 py-24 sm:py-32">
        <div className="max-w-3xl">
          <Mono>Joining the network</Mono>
          <Swap k={`close-${audience}`}>
            <h2 className="mt-4 font-cabinet text-[34px] sm:text-[50px] leading-[1.02] tracking-[-0.035em] font-semibold text-white">
              {audience === "lender"
                ? "Tell us what you fund. We'll bring you cases that fit."
                : "Bring us the deal. We'll do the rest properly."}
            </h2>
            <p className="mt-5 text-[15.5px] sm:text-[17px] leading-relaxed text-slate-400 max-w-2xl">
              {audience === "lender"
                ? "Applications are read by a person, usually the same working day. If it's a fit, your desk is set up, your agreement and NDA are issued pre-signed, and your criteria go live on the router."
                : "Applications are read by a person, usually the same working day. If it's a fit, your portal, your application link and your commission terms are live the same day you countersign."}
            </p>
          </Swap>
        </div>

        {/* Tier 1 — the loud one */}
        <div className="mt-10 flex flex-wrap items-center gap-3">
          <Link to="/introducer/apply" data-testid="close-apply-introducer"
                className="group inline-flex items-center gap-2 h-14 px-7 rounded-full bg-[#0055FF] hover:bg-[#0044CC] text-white text-[15.5px] font-semibold transition-colors shadow-[0_16px_50px_-14px_rgba(0,85,255,0.9)]">
            Apply as an introducer
            <ArrowRight className="size-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
          <Link to="/lender/apply" data-testid="close-apply-lender"
                className="group inline-flex items-center gap-2 h-14 px-7 rounded-full bg-white hover:bg-slate-100 text-[#0B1220] text-[15.5px] font-semibold transition-colors">
            Apply as a lender
            <ArrowRight className="size-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
          <Link to="/partner-with-us" data-testid="close-partner-with-us"
                className="group inline-flex items-center gap-2 h-14 px-6 rounded-full border border-white/15 hover:border-white/35 text-white text-[15.5px] font-medium transition-colors">
            Call centres &amp; media partners
            <ArrowRight className="size-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </div>

        {/* Tiers 2 + 3 */}
        <div className="mt-12 grid lg:grid-cols-2 gap-5">
          <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-7">
            <MessageSquare className="size-5 text-[#7da9ff]" strokeWidth={1.7} />
            <h3 className="mt-4 text-[17px] font-semibold text-white">Not ready to apply?</h3>
            <p className="mt-2 text-[13.5px] leading-relaxed text-slate-400">
              Ask us anything first — what we'd do with a live case, how commission works, what our lenders fund.
              A person replies, not a sequence.
            </p>
            <Button onClick={() => setEnquiry(true)} data-testid="close-open-enquiry"
                    className="mt-5 h-11 rounded-full bg-white/[0.08] hover:bg-white/[0.16] text-white font-semibold">
              Talk to the desk
            </Button>
          </div>

          <DeckCard />
        </div>

        <div className="mt-12 flex items-center gap-3 text-[12.5px] text-slate-500">
          Already a member?
          <Link to="/introducer/login" className="text-[#7da9ff] hover:text-white underline underline-offset-2 transition-colors" data-testid="close-introducer-signin">Introducer portal</Link>
          <span className="text-slate-700">·</span>
          <Link to="/lender/login" className="text-[#7da9ff] hover:text-white underline underline-offset-2 transition-colors" data-testid="close-lender-signin">Lender desk</Link>
        </div>
      </div>

      <EnquiryDialog open={enquiry} onOpenChange={setEnquiry} audience={audience} />
    </section>
  );
}

function DeckCard() {
  const { audience } = useAudience();
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const ref = useRef(null);
  const [stale] = useState(() => new URLSearchParams(window.location.search).get("deck"));

  // Someone clicked a deck link that this environment can't serve — walk them straight to a fresh one.
  useEffect(() => {
    if (!stale) return;
    const id = setTimeout(() => ref.current?.scrollIntoView({ behavior: "smooth", block: "center" }), 600);
    return () => clearTimeout(id);
  }, [stale]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/network/deck", { email: email.trim(), company: company.trim() || null, kind: audience });
      setDone(r.data.download);
      toast.success("Deck on its way — check your inbox");
      window.open(r.data.download, "_blank", "noopener");
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || "Couldn't send that — try again");
    } finally { setBusy(false); }
  };

  return (
    <div ref={ref} id="deck" className="rounded-2xl border border-white/10 bg-white/[0.03] p-7" data-testid="close-deck-card">
      <Download className="size-5 text-[#7da9ff]" strokeWidth={1.7} />
      <h3 className="mt-4 text-[17px] font-semibold text-white">Get the network deck</h3>
      <p className="mt-2 text-[13.5px] leading-relaxed text-slate-400">
        How the network works, what members get, and the market it sits in — one PDF, no sales sequence.
      </p>

      {stale && !done && (
        <div className="mt-5 rounded-xl border border-amber-400/25 bg-amber-400/[0.07] p-4" data-testid="deck-stale-notice">
          <p className="text-[13px] leading-relaxed text-amber-200/90">
            {stale === "expired"
              ? "That deck link has passed its 30 days."
              : "That deck link couldn't be opened here."}{" "}
            Pop your email in below and we'll send a fresh copy straight away.
          </p>
        </div>
      )}

      {done ? (
        <div className="mt-5 flex items-center gap-3">
          <span className="inline-flex items-center gap-2 text-[13px] text-emerald-400 font-semibold">
            <Check className="size-4" /> Sent to your inbox
          </span>
          <a href={done} target="_blank" rel="noopener noreferrer" data-testid="deck-download-again"
             className="text-[13px] text-[#7da9ff] underline underline-offset-2 hover:text-white transition-colors">
            Download again
          </a>
        </div>
      ) : (
        <form onSubmit={submit} className="mt-5 space-y-2.5">
          <div className="grid sm:grid-cols-2 gap-2.5">
            <Input required type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                   placeholder="you@company.co.uk" className={`h-11 ${dark}`} data-testid="deck-email" />
            <Input value={company} onChange={(e) => setCompany(e.target.value)}
                   placeholder="Company (optional)" className={`h-11 ${dark}`} data-testid="deck-company" />
          </div>
          <Button type="submit" disabled={busy} data-testid="deck-submit"
                  className="w-full sm:w-auto h-11 rounded-full bg-white/[0.08] hover:bg-white/[0.16] text-white font-semibold">
            {busy ? <Loader2 className="size-4 animate-spin" /> : "Send me the deck"}
          </Button>
          <p className="text-[11px] text-slate-600">
            One email with the deck. No sequence, and we don't pass your details on.
          </p>
        </form>
      )}
    </div>
  );
}

function EnquiryDialog({ open, onOpenChange, audience }) {
  const [f, setF] = useState({ name: "", company: "", email: "", phone: "", message: "" });
  const [kind, setKind] = useState(audience);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/network/enquiry", {
        kind, name: f.name.trim(), company: f.company.trim() || null, email: f.email.trim(),
        phone: f.phone.trim() || null, message: f.message.trim() || null,
      });
      setSent(true);
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || "Couldn't send that — try again");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) setSent(false); }}>
      <DialogContent className="max-w-lg bg-[#0B1220] border-white/12 text-slate-200" data-testid="enquiry-dialog">
        <DialogHeader>
          <DialogTitle className="font-cabinet text-[22px] tracking-[-0.02em] text-white">
            {sent ? "Got it — thank you" : "Talk to the desk"}
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            {sent
              ? "A person from the desk will come back to you, usually the same working day. Check your inbox for a confirmation."
              : "Tell us who you are and what you're weighing up. No sales sequence, no chasing."}
          </DialogDescription>
        </DialogHeader>

        {!sent && (
          <form onSubmit={submit} className="space-y-3">
            <div className="flex gap-2">
              {[["introducer", "I introduce deals"], ["lender", "I fund deals"], ["unsure", "Not sure yet"]].map(([k, label]) => (
                <button key={k} type="button" onClick={() => setKind(k)} data-testid={`enquiry-kind-${k}`}
                        className={`flex-1 h-9 rounded-full border text-[12px] font-semibold transition-colors ${
                          kind === k ? "bg-[#0055FF] border-[#0055FF] text-white" : "border-white/12 text-slate-400 hover:border-white/30"}`}>
                  {label}
                </button>
              ))}
            </div>
            <div className="grid sm:grid-cols-2 gap-2.5">
              <Input required value={f.name} onChange={set("name")} placeholder="Your name"
                     className={`h-11 ${dark}`} data-testid="enquiry-name" />
              <Input value={f.company} onChange={set("company")} placeholder="Company"
                     className={`h-11 ${dark}`} data-testid="enquiry-company" />
              <Input required type="email" value={f.email} onChange={set("email")} placeholder="Email"
                     className={`h-11 ${dark}`} data-testid="enquiry-email" />
              <Input value={f.phone} onChange={set("phone")} placeholder="Phone (optional)"
                     className={`h-11 ${dark}`} data-testid="enquiry-phone" />
            </div>
            <Textarea value={f.message} onChange={set("message")} data-testid="enquiry-message"
                      placeholder="What would you like to know? A live case you're weighing up is the fastest way in."
                      className={`min-h-[96px] ${dark}`} />
            <DialogFooter className="gap-2 pt-1">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}
                      className="rounded-full border-white/15 bg-transparent text-white hover:bg-white/[0.06]">
                Cancel
              </Button>
              <Button type="submit" disabled={busy} data-testid="enquiry-submit"
                      className="rounded-full bg-[#0055FF] hover:bg-[#0044CC] font-semibold">
                {busy ? <Loader2 className="size-4 animate-spin" /> : "Send it"}
              </Button>
            </DialogFooter>
          </form>
        )}

        {sent && (
          <div className="pt-2">
            <Button onClick={() => onOpenChange(false)} data-testid="enquiry-close"
                    className="w-full h-11 rounded-full bg-white/[0.08] hover:bg-white/[0.16] text-white font-semibold">
              Close
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
